import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardBody, Col, Container, Form, FormGroup, Row } from "reactstrap";
import { MetaTags } from "react-meta-tags";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import SfLoadingSkeleton from "components/Common/Loadings/SfLoadingSkeleton";
import BootstrapTable from 'react-bootstrap-table-next';
import { AppConfiguration } from 'config';
import { useDispatch, useSelector } from "react-redux";
import filterFactory from 'react-bootstrap-table2-filter';
import { customSortCaret } from "helpers/bootstrap_table_helper";
import CustomProspectFilters from "components/Common/CustomFilters";
import { getActiveProspectFilters, onClickLoadMoreData, onClickOpenFilterModal, onClickRemoveFilter, onCloseFilterModal, onSubmitOpenFilterModal } from "components/Common/CustomFilters/CommonFiltersUtils";
import ShowAppliedFilters from "components/Common/CustomFilters/ShowAppliedFilters";
import AdminUserProfileBreadCrumb from "../Authentication/AdminUserProfileBreadCrumb";
import { getAdminCampaignFulfillment, exportAdminCampaignFulfillment } from "store/admin/admin-campaign-fulfillment/actions";
import ToastAlert, { ToastAlertIconType } from "components/Common/ToastAlert/ToastAlert";
import { ADMIN_CAMPAIGN_FULFILLMENT_FILTER_SPECIAL_CASES, adminCampaignFulFillmentColumns, getAdminCampaignFulfillmentFilterObjects } from "./admin_campaign_fulfillment_helper";
import { convertStringifiedNullToNull, REQUIRED_FEATURE_FLAGS } from "helpers/sf_constants";
import { sanitizeProspectFilters } from "helpers/sf_utils";
import { mapActiveFilters } from "helpers/conversation_filter_helper";
import { postAdminUserLogin } from "store/admin/user-login/actions";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import useFeatureFlags from "hooks/useFeatureFlags";
import OffsetPagination from "components/Common/OffsetPagination";

const AdminCampaignFulFillment = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const [isFeatureFlagActive] = useFeatureFlags();
    const { admin_campaign_fulfillment, loading, export_loading, has_next_page, has_lookahead_pages, total_number_of_pages, last_page_fetched, accountManagerOptions, current_admin_user } = useSelector((state) => ({
        admin_campaign_fulfillment: state.AdminCampaignFulFillment.admin_campaign_fulfillment,
        loading: state.AdminCampaignFulFillment.loading,
        export_loading: state.AdminCampaignFulFillment.export_loading,
        has_next_page: state.AdminCampaignFulFillment.has_next_page,
        has_lookahead_pages: state.AdminCampaignFulFillment.has_lookahead_pages,
        total_number_of_pages: state.AdminCampaignFulFillment.total_number_of_pages,
        last_page_fetched: state.AdminCampaignFulFillment.last_page_fetched,
        accountManagerOptions: state.AdminManageInternalUsers.all_admin_employees,
        current_admin_user: state.LoginAdminUser.current_user,
    }));
    const [isActiveFilterModal, setIsActiveFilterModal] = useState(false);
    // Initialize filter items with default "today" timeframe
    const [prospectFilterItems, setProspectFilterItems] = useState([]);
    const currentSortRef = useRef({ sort_column: null, sort_direction: null });
    const sortHandlerRef = useRef(null);
    const lastTableDataRef = useRef([]);
    const shouldUseOffsetPagination = useMemo(() => (
        isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_OFFSET_PAGINATION_APPLICATION)
    ), [isFeatureFlagActive])

    useEffect(() => {
        const defaultFilterObjects = getAdminCampaignFulfillmentFilterObjects(accountManagerOptions);
        setProspectFilterItems(Object.values(defaultFilterObjects).filter(item => item.is_active));
    }, [accountManagerOptions])

    useEffect(() => {
        // Fetch data with default timeframe filter on initial load
        const activeFilteredColumns = { data: null };
        if (prospectFilterItems?.length) {
            activeFilteredColumns.data = mapActiveFilters(sanitizeProspectFilters(prospectFilterItems), ADMIN_CAMPAIGN_FULFILLMENT_FILTER_SPECIAL_CASES);
        }
        const queryParams = { page_number: 1 };
        if (activeFilteredColumns.data && Object.keys(activeFilteredColumns.data)?.length) {
            queryParams.filters = JSON.stringify(convertStringifiedNullToNull(activeFilteredColumns.data) || {});
        }
        dispatch(getAdminCampaignFulfillment({
            queryParams,
            appendResults: false,
            useCache: shouldUseOffsetPagination,
        }, true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getCurrentFilterModal = () => {
        if (!prospectFilterItems?.length) return null;
        return mapActiveFilters(sanitizeProspectFilters(prospectFilterItems), ADMIN_CAMPAIGN_FULFILLMENT_FILTER_SPECIAL_CASES);
    }

    const fetchAdminCampaignFulfillmentPage = (pageNumber = 1, filterModal = null, appendResults = false) => {
        const queryParams = { page_number: pageNumber };
        if (filterModal && Object.keys(filterModal)?.length) {
            queryParams.filters = JSON.stringify(convertStringifiedNullToNull(filterModal) || {});
        }
        const { sort_column, sort_direction } = currentSortRef.current;
        if (sort_column) {
            queryParams.sort_column = sort_column;
            queryParams.sort_direction = sort_direction || 'desc';
        }
        return dispatch(getAdminCampaignFulfillment({
            queryParams,
            appendResults,
            useCache: shouldUseOffsetPagination,
        }, true));
    }

    const fetchNextBatchWrapper = (filterModal = null, last_page_fetched = 0, options = {}) => {
        if (loading) {
            return;
        }
        const pageNumber = Number.isFinite(Number(options.page_number))
            ? Number(options.page_number)
            : (last_page_fetched + 1);
        const appendResults = typeof options.appendResults === "boolean" ? options.appendResults : last_page_fetched > 0;
        return fetchAdminCampaignFulfillmentPage(pageNumber, filterModal, appendResults);
    }

    const onSubmitOpenFilterModalLocal = (values) => {
        onSubmitOpenFilterModal(
            values,
            setIsActiveFilterModal,
            setProspectFilterItems,
            fetchNextBatchWrapper,
            ADMIN_CAMPAIGN_FULFILLMENT_FILTER_SPECIAL_CASES
        );
    };

    const onClickUserLogin = (account_id) => {
        dispatch(postAdminUserLogin({ user: { account: account_id }, history: history }))
    }

    const onClickExportFulfillment = () => {
        if (export_loading) {
            return;
        }
        if (!admin_campaign_fulfillment?.length) {
            ToastAlert.fire({ icon: ToastAlertIconType.ERROR, title: 'No data available for the applied filters' });
            return;
        }
        const filterModal = filterCount > 0 ? getCurrentFilterModal() : null;
        const queryParams = {};
        if (filterModal && Object.keys(filterModal).length) {
            queryParams.filters = JSON.stringify(convertStringifiedNullToNull(filterModal) || {});
        }
        dispatch(exportAdminCampaignFulfillment({ queryParams }));
    }

    // Always keep the latest sort logic in the ref so the stable column callback never goes stale
    sortHandlerRef.current = (field, order) => {
        currentSortRef.current = { sort_column: field, sort_direction: order };
        const filterModal = filterCount ? getCurrentFilterModal() : null;
        fetchAdminCampaignFulfillmentPage(1, filterModal, false);
    };

    const columns = useMemo(() => {
        const hideTotalSpent = current_admin_user?.admin_role?.name?.toLowerCase() === 'account manager';
        return adminCampaignFulFillmentColumns(onClickUserLogin, false, hideTotalSpent);
    }, [current_admin_user]);

    // Sorting is remote: the table only emits the sort event and does NOT
    // re-sort the current page client-side. We fetch the sorted page from the
    // backend here (single source of truth).
    const handleTableChange = (type, { sortField, sortOrder }) => {
        if (type !== 'sort') return;
        sortHandlerRef.current(sortField, sortOrder);
    };

    const { filterCount, activeProspectFilterItems } = useMemo(() => {
        return getActiveProspectFilters(prospectFilterItems)
    }, [prospectFilterItems])

    // Keep the last non-empty dataset only while a fetch is in flight so the
    // table never unmounts/flashes during a re-fetch. Once loading finishes,
    // honor a genuinely empty result so the "no data" indication can show.
    if (admin_campaign_fulfillment?.length) {
        lastTableDataRef.current = admin_campaign_fulfillment;
    } else if (!loading) {
        lastTableDataRef.current = admin_campaign_fulfillment || [];
    }
    const tableData = lastTableDataRef.current;

    const currentPage = Math.max(1, Number(last_page_fetched) || 1)
    const knownTotalPages = Math.max(Number(total_number_of_pages) || 0, currentPage)
    const paginationTotal = shouldUseOffsetPagination
        ? (knownTotalPages > 0
            ? knownTotalPages
            : (has_next_page
                ? currentPage + (has_lookahead_pages ? 8 : 1)
                : currentPage))
        : Math.max(Number(total_number_of_pages) || 0, currentPage)

    return (
        <React.Fragment>
            <div className="page-content" id="page-content-wrapper">
                <MetaTags>
                    <title> Campaign Fulfillment | {AppConfiguration.WEBSITE}</title>
                </MetaTags>
                <CustomProspectFilters
                    isActive={isActiveFilterModal}
                    filterItems={prospectFilterItems}
                    onSubmitModalState={onSubmitOpenFilterModalLocal}
                    onClickModalClose={() => onCloseFilterModal(setIsActiveFilterModal)}
                    isActiveSearchBox={false}
                    customMarginTop='sfpro-filter-modal-margin-top'
                />
                <Container fluid>
                    <AdminUserProfileBreadCrumb title="Admin" breadcrumbItem="Campaign Fulfillment" />
                    <Row>
                        <Col md={12}>
                            <Card>
                                <CardBody>
                                    <ToolkitProvider
                                        keyField="id"
                                        columns={columns}
                                        data={tableData}
                                        exportCSV={{
                                            fileName: "CampaignFulfillment.csv",
                                            exportAll: false,
                                        }}
                                    >
                                        {(toolkitProps) => (
                                            <>
                                                <Form>
                                                    <Row className="d-flex align-items-end">
                                                        <Col md="auto" className="align-self-end">
                                                            <FormGroup className="mt-1 mb-0">
                                                                <div>
                                                                    <button
                                                                        className={`d-flex align-items-center btn btn-outline-primary`}
                                                                        type='button'
                                                                        onClick={() => {
                                                                            onClickOpenFilterModal(setIsActiveFilterModal, setProspectFilterItems, getAdminCampaignFulfillmentFilterObjects(accountManagerOptions));
                                                                        }}
                                                                    >
                                                                        <i className="bx bx-filter font-size-18 align-middle me-2" />
                                                                        Sort & Filter
                                                                    </button>
                                                                </div>
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md="auto" className="flex-nowrap mb-3" style={{ maxHeight: '100px' }}>
                                                            <ShowAppliedFilters filtered_columns={activeProspectFilterItems} onClickRemoveFilter={(field_name) => onClickRemoveFilter(field_name, setProspectFilterItems, fetchNextBatchWrapper,
                                                                ADMIN_CAMPAIGN_FULFILLMENT_FILTER_SPECIAL_CASES
                                                            )} />
                                                        </Col>
                                                        <Col md="auto" className="align-self-end ms-auto">
                                                            <FormGroup className="mt-1 mb-0">
                                                                <div>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-sfblue"
                                                                        disabled={export_loading || !admin_campaign_fulfillment?.length}
                                                                        onClick={onClickExportFulfillment}
                                                                    >
                                                                        <i className="bx bx-import font-size-14 align-middle me-2" />
                                                                        {export_loading ? 'Queuing...' : 'Export'}
                                                                    </button>
                                                                </div>
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>
                                                </Form>
                                                <Row>
                                                    <Col xl="12">
                                                        <div className="sfTable-Container">
                                                            {(loading && !tableData.length) ? (<SfLoadingSkeleton rows={8} cols={6} />) :
                                                                (<BootstrapTable
                                                                    keyField="id"
                                                                    columns={columns}
                                                                    data={tableData}
                                                                    responsive
                                                                    bordered={false}
                                                                    striped={false}
                                                                    rowClasses="tr-hover"
                                                                    filter={filterFactory()}
                                                                    headerClasses="sftableHeader text-black thead-h50"
                                                                    classes="table align-middle table-nowrap text-black"
                                                                    headerWrapperClasses="thead-light"
                                                                    noDataIndication={`You currently don't have any logs`}
                                                                    sort={{ sortCaret: customSortCaret }}
                                                                    remote={{ sort: true }}
                                                                    onTableChange={handleTableChange}
                                                                    {...toolkitProps.baseProps}
                                                                />)}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </>
                                        )}
                                    </ToolkitProvider>
                                    {shouldUseOffsetPagination ? (
                                        <OffsetPagination
                                            currentPage={currentPage}
                                            totalPages={paginationTotal}
                                            hasNextPage={has_next_page}
                                            hasDeepOffsetPages={has_lookahead_pages}
                                            offsetLookAheadPages={8}
                                            loading={loading}
                                            onPageChange={(p) => {
                                                const parsedPage = Number(p)
                                                if (!Number.isFinite(parsedPage) || parsedPage < 1) {
                                                    return
                                                }
                                                const activeFilters = filterCount > 0
                                                    ? mapActiveFilters(
                                                        sanitizeProspectFilters(prospectFilterItems),
                                                        ADMIN_CAMPAIGN_FULFILLMENT_FILTER_SPECIAL_CASES
                                                    )
                                                    : null
                                                fetchNextBatchWrapper(activeFilters, 0, { page_number: parsedPage, appendResults: false })
                                            }}
                                            className="campaign-analysis-pagination mt-3 align-items-center justify-content-end"
                                        />
                                    ) : (
                                        <Col xl="12">
                                            <div className="float-end">
                                                <p className="mx-3">
                                                    {has_next_page && (
                                                        <a className='btn btn-outline-sfblue'
                                                            onClick={() => onClickLoadMoreData(prospectFilterItems, filterCount, last_page_fetched, fetchNextBatchWrapper, ADMIN_CAMPAIGN_FULFILLMENT_FILTER_SPECIAL_CASES)}>{loading ? 'Please wait...' : 'Load More'}</a>
                                                    )
                                                    }
                                                </p>
                                            </div>
                                        </Col>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    {/* Table ends */}
                </Container>
            </div>
        </React.Fragment>
    )
}

export default AdminCampaignFulFillment
